import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Swords, Trophy, Shield } from "lucide-react";
import { Loader, btn } from "@/components/Page";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";

export default function FighterProfile() {
  const { fighterId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [fighter, setFighter] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const [contractDetails, setContractDetails] = useState(null);

  useEffect(() => {
    loadFighter();
  }, [fighterId]);

  const loadFighter = async () => {
    try {
      const { data } = await api.get(`/arena/fighters/${fighterId}`);
      setFighter(data.fighter);
      setIsOwner(data.is_owner);
      
      // If fighter has a contract, fetch contract details
      if (data.fighter.current_contract) {
        try {
          const contractRes = await api.get(`/arena/contracts/${data.fighter.current_contract}`);
          setContractDetails(contractRes.data.contract);
        } catch (e) {
          // Contract fetch failed, but continue showing fighter
          console.error("Failed to load contract details:", e);
        }
      }
    } catch (e) {
      toast.error(apiErr(e));
      navigate("/arena-circuit/fighters");
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loader />;
  if (!fighter) return null;

  const statBarColor = (value) => {
    if (value >= 250) return "bg-emerald-500";
    if (value >= 200) return "bg-amber-500";
    if (value >= 150) return "bg-orange-500";
    return "bg-zinc-600";
  };

  const statBarWidth = (value) => {
    return `${(value / 300) * 100}%`;
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate("/arena-circuit/fighters")}
          className={btn() + " bg-zinc-800 hover:bg-zinc-700"}
        >
          <ArrowLeft size={16} />
        </button>
        <div className="flex-1">
          <h1 className="font-cinzel text-3xl text-amber-300 tracking-wide mb-1">
            {fighter.name}
          </h1>
          <p className="text-zinc-400 capitalize">{fighter.cls}</p>
        </div>
        {fighter.corona_holder && (
          <Trophy size={32} className="text-amber-400" title="Corona Holder" />
        )}
      </div>

      {/* Status Banner */}
      {fighter.current_contract ? (
        <div className="bg-emerald-950/20 border border-emerald-700/40 rounded p-4">
          <div className="flex items-center gap-2 text-emerald-300 mb-2">
            <Swords size={16} />
            <span className="font-cinzel text-sm uppercase tracking-wide">Under Contract</span>
          </div>
          {contractDetails && (
            <div className="mt-3 pt-3 border-t border-emerald-700/30">
              <div className="text-sm text-zinc-300">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-zinc-400">Circuit:</span>
                  <span className="text-amber-300 font-cinzel">{contractDetails.circuit_name}</span>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-zinc-400">Fights Completed:</span>
                  <span className="text-emerald-300 font-semibold">
                    {contractDetails.num_fights - contractDetails.fights_remaining} / {contractDetails.num_fights}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-zinc-400">Fights Remaining:</span>
                  <span className="text-amber-300 font-semibold">{contractDetails.fights_remaining}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-zinc-900/50 border border-zinc-800 rounded p-4">
          <div className="flex items-center gap-2 text-zinc-500">
            <Shield size={16} />
            <span className="font-cinzel text-sm uppercase tracking-wide">Free Agent</span>
          </div>
        </div>
      )}

      {/* Record */}
      <div className="border border-amber-900/30 bg-zinc-950 p-6">
        <h3 className="font-cinzel text-xl text-amber-300 mb-4 uppercase tracking-wide">Fight Record</h3>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-3xl font-cinzel text-emerald-400">{fighter.record.wins}</div>
            <div className="text-xs uppercase tracking-widest text-zinc-500">Wins</div>
          </div>
          <div>
            <div className="text-3xl font-cinzel text-red-400">{fighter.record.losses}</div>
            <div className="text-xs uppercase tracking-widest text-zinc-500">Losses</div>
          </div>
          <div>
            <div className="text-3xl font-cinzel text-amber-400">{fighter.record.bouts}</div>
            <div className="text-xs uppercase tracking-widest text-zinc-500">Total Bouts</div>
          </div>
        </div>
      </div>

      {/* Specializations */}
      <div className="border border-amber-900/30 bg-zinc-950 p-6">
        <h3 className="font-cinzel text-xl text-amber-300 mb-4 uppercase tracking-wide">Specializations</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-zinc-900/50 border border-zinc-800 p-4">
            <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Class Specialist</div>
            <div className="text-amber-300 font-cinzel capitalize">
              {fighter.class_specialist.replace(/_/g, " ")}
            </div>
          </div>
          <div className="bg-zinc-900/50 border border-zinc-800 p-4">
            <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Tactic Specialist</div>
            <div className="text-amber-300 font-cinzel capitalize">
              {fighter.tactic_specialist}
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="border border-amber-900/30 bg-zinc-950 p-6">
        <h3 className="font-cinzel text-xl text-amber-300 mb-4 uppercase tracking-wide">Stats</h3>
        <div className="space-y-4">
          {Object.entries(fighter.stats).map(([stat, value]) => (
            <div key={stat}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm capitalize text-zinc-400 font-cinzel">{stat}</span>
                <span className="text-sm text-zinc-200 font-semibold tabular-nums">{value}</span>
              </div>
              <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all ${statBarColor(value)}`}
                  style={{ width: statBarWidth(value) }}
                />
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 pt-4 border-t border-zinc-800 text-center">
          <span className="text-sm text-zinc-500">Total Stats:</span>
          <span className="ml-2 text-lg font-cinzel text-amber-400">
            {Object.values(fighter.stats).reduce((sum, val) => sum + val, 0)}
          </span>
        </div>
      </div>

      {/* Owner Info */}
      {!isOwner && (
        <div className="bg-zinc-900/50 border border-zinc-800 rounded p-4 text-center">
          <p className="text-sm text-zinc-400">
            Owned by <span className="text-amber-300 font-semibold">{fighter.user_name}</span>
          </p>
        </div>
      )}
    </div>
  );
}
